import { AlertCircle, Home, RefreshCcw } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";

export interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary?: () => void;
  variant?: "page" | "section" | "inline";
}

/**
 * エラー発生時に表示するフォールバックUI
 * - page: ページ全体を占有するエラー表示
 * - section: セクション内のエラー表示
 * - inline: インラインのエラー表示
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  variant = "page",
}) => {
  const { t } = useTranslation();
  const isDevelopment = import.meta.env.DEV;

  const handleRetry = () => {
    resetErrorBoundary?.();
  };

  const handleGoHome = () => {
    window.location.href = "/dashboard";
  };

  if (variant === "inline") {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        role="alert"
        aria-live="polite"
      >
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{t("error.inlineMessage")}</span>
        {resetErrorBoundary && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRetry}
            className="ml-auto h-7 px-2 text-destructive hover:bg-destructive/20 hover:text-destructive"
          >
            {t("error.retry")}
          </Button>
        )}
      </div>
    );
  }

  if (variant === "section") {
    return (
      <div
        className="flex flex-col items-center justify-center rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        role="alert"
        aria-live="polite"
      >
        <AlertCircle className="mb-3 h-10 w-10 text-destructive" aria-hidden="true" />
        <h3 className="mb-2 text-lg font-semibold text-foreground">{t("error.sectionTitle")}</h3>
        <p className="mb-4 text-sm text-muted-foreground">{t("error.sectionMessage")}</p>
        {isDevelopment && (
          <details className="mb-4 w-full max-w-md text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              {t("error.showDetails")}
            </summary>
            <pre className="mt-2 overflow-auto rounded-md bg-muted p-3 text-xs">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
        {resetErrorBoundary && (
          <Button onClick={handleRetry} variant="outline" size="sm">
            <RefreshCcw className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("error.retry")}
          </Button>
        )}
      </div>
    );
  }

  // variant === "page"
  return (
    <div
      className={cn("flex min-h-[50vh] flex-col items-center justify-center p-6 text-center")}
      role="alert"
      aria-live="polite"
    >
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
      </div>

      <h1 className="mb-2 text-2xl font-bold text-foreground">{t("error.pageTitle")}</h1>
      <p className="mb-6 max-w-md text-muted-foreground">{t("error.pageMessage")}</p>

      {isDevelopment && (
        <details className="mb-6 w-full max-w-lg text-left">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
            {t("error.showDetails")}
          </summary>
          <pre className="mt-2 overflow-auto rounded-md bg-muted p-4 text-xs">
            <strong>{error.name}:</strong> {error.message}
            {error.stack && (
              <>
                {"\n\n"}
                <span className="text-muted-foreground">{error.stack}</span>
              </>
            )}
          </pre>
        </details>
      )}

      <div className="flex flex-wrap gap-3">
        {resetErrorBoundary && (
          <Button onClick={handleRetry} variant="default">
            <RefreshCcw className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("error.retry")}
          </Button>
        )}
        <Button onClick={handleGoHome} variant="outline">
          <Home className="mr-2 h-4 w-4" aria-hidden="true" />
          {t("error.goHome")}
        </Button>
      </div>
    </div>
  );
};

export default ErrorFallback;
